import { Injectable } from '@angular/core';
import { Editor, TOOLBAR_MINIMAL, Toolbar, marks as basicMarks, nodes as basicNodes, schema } from 'ngx-editor';
import { Mark, MarkSpec, NodeSpec, Schema } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import { DOMParser } from 'prosemirror-model';
import { tableNodes } from 'prosemirror-tables';
import emailTemplateLinter from '../prosemirror/emailTemplateLinter';




@Injectable({
  providedIn: 'root'
})
export class ProseMirrorEditorService {

  constructor() { }

  readonly spanWithClassMark: MarkSpec = {
    attrs: {
      class: { default: "" }
    },
    parseDOM: [
      {
        tag: 'span[class]',
        getAttrs(dom: any) {
          return { class: dom.getAttribute('class') };
        },
      },
    ],
    toDOM(mark: any) {
      return ['span', { class: mark.attrs.class }, 0];
    },
  }

  readonly squareBracketMark: MarkSpec = {
    name: "squareBracket",
    // attrs: {
    //   content: { default: "" },
    // },
    parseDOM: [
      {
        tag: "span.square-bracket",
        getAttrs(dom: any) {
          return {};
        },
      },
    ],
    toDOM(mark: Mark) {
      return ["span", { class: "square-bracket" }, 0];
    },
  };

  readonly spanWithClassNode: NodeSpec = {
    name: "span_with_class",
    attrs:
    {
      class: {default: ""},
      hidden: {default: false}
    },
   
   content: "inline*",
   inline: true,
   group: "inline",
    parseDOM: [{
      tag: 'span[class]',
      getAttrs(node: any) {
        return { class: node.getAttribute('class') };
      }
    }],
    toDOM(node: any) {
      return ["span", {class: node.attrs.class, style: node.attrs.hidden ? "display: none" : ""}, 0]
    }
  }

  getEmailTemplateEditor() {
    return new Editor({
      schema: new Schema({
        nodes: { 
          ...basicNodes, 
          span_with_class: this.spanWithClassNode
        },
        marks: {
          ...basicMarks,
          // spanWithClass: this.spanWithClassMark
        }

      }),
      plugins: [emailTemplateLinter]
    })
  }

  getBasicEditor() {
    return new Editor({
      schema: new Schema({
        nodes: {
          ...basicNodes
        },
        marks: {
          ...basicMarks
        }
      }),
      plugins: []
    })
  }

  
  getMinimalEditor() {
    const {image, blockquote,horizontal_rule,heading,code_block, ordered_list,bullet_list, ...nodes} = basicNodes
    const {text_background_color,text_color, ...marks} = basicMarks
    return new Editor({
      schema: new Schema({
        nodes: {
          ...nodes
        },
        marks: {
          ...marks
        }
      }),
      plugins: []
    })
  }

  getCustomerNoteEditor() {
    const {image, blockquote, horizontal_rule, ...nodes} = basicNodes
    const {...marks} = basicMarks

    return new Editor({
      schema: new Schema({
        nodes: {
          ...nodes
        },
        marks: {
          ...marks
        }
      }),
      plugins: []
    })
  }

  getEmailTemplateToolbar(): Toolbar {
  return [
    ['bold', 'italic', 'underline'],
    [{heading: ['h1','h2', 'h3', 'h4', 'h5', 'h6']}],
    ['link', 'image'],
    ['ordered_list', 'bullet_list'],
    ['text_color', 'background_color'],
    ['format_clear']
  ]
  }

  getCustomerNoteToolbar(): Toolbar {
    return [
      [{heading: ['h1','h2', 'h3', 'h4', 'h5', 'h6']}],
      ['bold', 'italic', 'underline'],
      ['text_color', 'background_color'],
      ['ordered_list', 'bullet_list'],
      ['link', 'code'],
      ['format_clear']
    ]
  }

  getMinimalToolbar(): Toolbar {
    return [
      ['bold', 'italic'],
      ['underline', 'strike'],
      [ 'format_clear'],
    ]
  }

  getMainSiteContentEditor() {
    const {image, blockquote,horizontal_rule,heading,code_block, ...nodes} = basicNodes
    const {text_background_color,text_color,code, link, ...marks} = basicMarks
    return new Editor({
      schema: new Schema({
        nodes: {
          ...nodes
        },
        marks: {
          ...marks
        }
      }),
      plugins: []
    })
  }

  getMainSiteContentToolbar(): Toolbar {
    return [
      ['bold', 'italic'],
      ['underline', 'strike'],
      ['ordered_list', 'bullet_list'],
      ['format_clear'],
    ]
  }

  getAlertEditor() {
    const {blockquote, horizontal_rule, ...nodes} = basicNodes
    const {...marks} = basicMarks

    // Create a more permissive schema for alert editor to preserve HTML styling
    const alertSchema = new Schema({
      nodes: {
        ...nodes,
        // Enhanced image node to preserve more attributes
        image: {
          ...nodes.image,
          attrs: {
            ...nodes.image.attrs,
            style: { default: null },
            class: { default: null },
            width: { default: null },
            height: { default: null }
          },
          parseDOM: [
            {
              tag: 'img[src]',
              getAttrs: (dom: any) => ({
                src: dom.getAttribute('src'),
                title: dom.getAttribute('title'),
                alt: dom.getAttribute('alt'),
                style: dom.getAttribute('style'),
                class: dom.getAttribute('class'),
                width: dom.getAttribute('width'),
                height: dom.getAttribute('height')
              })
            }
          ],
          toDOM: (node: any) => {
            const attrs: any = { src: node.attrs.src };
            if (node.attrs.title) attrs.title = node.attrs.title;
            if (node.attrs.alt) attrs.alt = node.attrs.alt;
            if (node.attrs.style) attrs.style = node.attrs.style;
            if (node.attrs.class) attrs.class = node.attrs.class;
            if (node.attrs.width) attrs.width = node.attrs.width;
            if (node.attrs.height) attrs.height = node.attrs.height;
            return ['img', attrs];
          }
        },
        // Enhanced table nodes to preserve styling
        ...tableNodes({
          tableGroup: 'block',
          cellContent: 'block+',
          cellAttributes: {
            style: { default: null },
            colspan: { default: null },
            rowspan: { default: null }
          }
        }),
        // Enhanced list nodes to preserve styling
        bullet_list: {
          ...nodes.bullet_list,
          attrs: {
            style: { default: null }
          },
          parseDOM: [
            {
              tag: 'ul',
              getAttrs: (dom: any) => ({
                style: dom.getAttribute('style')
              })
            }
          ],
          toDOM: (node: any) => {
            const attrs: any = {};
            if (node.attrs.style) attrs.style = node.attrs.style;
            return ['ul', attrs, 0];
          }
        },
        ordered_list: {
          ...nodes.ordered_list,
          attrs: {
            style: { default: null }
          },
          parseDOM: [
            {
              tag: 'ol',
              getAttrs: (dom: any) => ({
                style: dom.getAttribute('style')
              })
            }
          ],
          toDOM: (node: any) => {
            const attrs: any = {};
            if (node.attrs.style) attrs.style = node.attrs.style;
            return ['ol', attrs, 0];
          }
        },
        list_item: {
          ...nodes.list_item,
          attrs: {
            style: { default: null }
          },
          parseDOM: [
            {
              tag: 'li',
              getAttrs: (dom: any) => ({
                style: dom.getAttribute('style')
              })
            }
          ],
          toDOM: (node: any) => {
            const attrs: any = {};
            if (node.attrs.style) attrs.style = node.attrs.style;
            return ['li', attrs, 0];
          }
        },
        // Enhanced heading nodes to preserve background colors
        heading: {
          ...nodes.heading,
          attrs: {
            ...nodes.heading.attrs,
            style: { default: null }
          },
          parseDOM: [
            {
              tag: 'h1, h2, h3, h4, h5, h6',
              getAttrs: (dom: any) => ({
                level: parseInt(dom.tagName.charAt(1)),
                style: dom.getAttribute('style')
              })
            }
          ],
          toDOM: (node: any) => {
            const attrs: any = {};
            if (node.attrs.style) attrs.style = node.attrs.style;
            return [`h${node.attrs.level}`, attrs, 0];
          }
        }
      },
      marks: {
        ...marks,
        // Enhanced text color and background color marks
        text_color: {
          ...marks.text_color,
          attrs: {
            color: { default: null },
            style: { default: null }
          },
          parseDOM: [
            {
              style: 'color',
              getAttrs: (value: string) => ({ color: value })
            },
            {
              tag: 'span[style*="color"]',
              getAttrs: (dom: any) => {
                const style = dom.getAttribute('style');
                const colorMatch = style?.match(/color:\s*([^;]+)/);
                return colorMatch ? { color: colorMatch[1], style: style } : null;
              }
            }
          ],
          toDOM: (mark: any) => {
            const attrs: any = {};
            if (mark.attrs.color) attrs.style = `color: ${mark.attrs.color}`;
            if (mark.attrs.style) attrs.style = mark.attrs.style;
            return ['span', attrs, 0];
          }
        },
        text_background_color: {
          ...marks.text_background_color,
          attrs: {
            color: { default: null },
            style: { default: null }
          },
          parseDOM: [
            {
              style: 'background-color',
              getAttrs: (value: string) => ({ color: value })
            },
            {
              tag: 'span[style*="background-color"]',
              getAttrs: (dom: any) => {
                const style = dom.getAttribute('style');
                const bgMatch = style?.match(/background-color:\s*([^;]+)/);
                return bgMatch ? { color: bgMatch[1], style: style } : null;
              }
            }
          ],
          toDOM: (mark: any) => {
            const attrs: any = {};
            if (mark.attrs.color) attrs.style = `background-color: ${mark.attrs.color}`;
            if (mark.attrs.style) attrs.style = mark.attrs.style;
            return ['span', attrs, 0];
          }
        }
      }
    });

    // Create a custom paste handling plugin
    const pasteHandlerPlugin = new Plugin({
      props: {
        handlePaste: (view, event, slice) => {
          const clipboardData = event.clipboardData;
          if (!clipboardData) return false;

          const htmlData = clipboardData.getData('text/html');
          if (htmlData) {
            // Create a temporary DOM element to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlData;
            
            // Preserve more HTML attributes and styles
            const preservedHtml = this.preserveHtmlStyles(tempDiv);
            
            // Parse the preserved HTML into the editor
            const parser = DOMParser.fromSchema(alertSchema);
            const doc = parser.parse(preservedHtml);
            
            if (doc) {
              const tr = view.state.tr.replaceSelectionWith(doc);
              view.dispatch(tr);
              return true;
            }
          }
          return false;
        }
      }
    });

    return new Editor({
      schema: alertSchema,
      plugins: [pasteHandlerPlugin]
    })
  }

  private preserveHtmlStyles(element: HTMLElement): HTMLElement {
    // Clone the element to avoid modifying the original
    const cloned = element.cloneNode(true) as HTMLElement;
    
    // Preserve inline styles on all elements
    const allElements = cloned.querySelectorAll('*');
    allElements.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      const originalStyle = htmlEl.getAttribute('style');
      if (originalStyle) {
        // Preserve background-color, color, and other important styles
        const preservedStyles = this.extractImportantStyles(originalStyle);
        if (preservedStyles) {
          htmlEl.setAttribute('style', preservedStyles);
        }
      }
    });
    
    // Special handling for table cells to preserve background colors
    const tableCells = cloned.querySelectorAll('td, th');
    tableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      const originalStyle = htmlCell.getAttribute('style');
      if (originalStyle && originalStyle.includes('background-color')) {
        // Ensure background-color is preserved
        const bgColorMatch = originalStyle.match(/background-color:\s*([^;]+)/);
        if (bgColorMatch) {
          const currentStyle = htmlCell.getAttribute('style') || '';
          if (!currentStyle.includes('background-color')) {
            htmlCell.setAttribute('style', `${currentStyle}; background-color: ${bgColorMatch[1]}`);
          }
        }
      }
    });
    
    // Special handling for headings to preserve background colors
    const headings = cloned.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading: Element) => {
      const htmlHeading = heading as HTMLElement;
      const originalStyle = htmlHeading.getAttribute('style');
      
      if (originalStyle && originalStyle.includes('background-color')) {
        // Ensure background-color is preserved
        const bgColorMatch = originalStyle.match(/background-color:\s*([^;]+)/);
        if (bgColorMatch) {
          const currentStyle = htmlHeading.getAttribute('style') || '';
          if (!currentStyle.includes('background-color')) {
            htmlHeading.setAttribute('style', `${currentStyle}; background-color: ${bgColorMatch[1]}`);
          }
        }
      } else {
        // Check if this heading should have a teal background based on content
        const headingText = htmlHeading.textContent?.toLowerCase() || '';
        const isSectionHeader = headingText.includes('operations') || 
                               headingText.includes('fundraising') || 
                               headingText.includes('human resources') || 
                               headingText.includes('marketing') || 
                               headingText.includes('recruiting') || 
                               headingText.includes('consulting') || 
                               headingText.includes('meetings/admin') ||
                               headingText.includes('ppm365') ||
                               headingText.includes('organizational leadership') ||
                               headingText.includes('operational standards');
        
        if (isSectionHeader) {
          // Apply teal background to section headers that don't have one
          const currentStyle = htmlHeading.getAttribute('style') || '';
          htmlHeading.setAttribute('style', `${currentStyle}; background-color: #55c1ac; color: #ffffff; font-weight: 700; padding: 0.5rem; font-family: Montserrat, sans-serif;`);
        }
      }
    });
    
    // Special handling for lists to preserve styling
    const lists = cloned.querySelectorAll('ul, ol');
    lists.forEach((list: Element) => {
      const htmlList = list as HTMLElement;
      const originalStyle = htmlList.getAttribute('style');
      if (originalStyle) {
        const preservedStyles = this.extractImportantStyles(originalStyle);
        if (preservedStyles) {
          htmlList.setAttribute('style', preservedStyles);
        }
      }
    });
    
    // Special handling for list items to preserve styling
    const listItems = cloned.querySelectorAll('li');
    listItems.forEach((item: Element) => {
      const htmlItem = item as HTMLElement;
      const originalStyle = htmlItem.getAttribute('style');
      if (originalStyle) {
        const preservedStyles = this.extractImportantStyles(originalStyle);
        if (preservedStyles) {
          htmlItem.setAttribute('style', preservedStyles);
        }
      }
    });
    
    // Special handling for images to preserve centering and other styling
    const images = cloned.querySelectorAll('img');
    images.forEach((img: Element) => {
      const htmlImg = img as HTMLElement;
      const originalStyle = htmlImg.getAttribute('style');
      if (originalStyle) {
        const preservedStyles = this.extractImportantStyles(originalStyle);
        if (preservedStyles) {
          htmlImg.setAttribute('style', preservedStyles);
        }
      }
      
      // Check if the image's parent has text-align: center
      const parent = htmlImg.parentElement;
      if (parent) {
        const parentStyle = parent.getAttribute('style');
        if (parentStyle && parentStyle.includes('text-align: center')) {
          // Apply centering to the image itself
          const currentStyle = htmlImg.getAttribute('style') || '';
          if (!currentStyle.includes('display: block') && !currentStyle.includes('margin: 0 auto')) {
            htmlImg.setAttribute('style', `${currentStyle}; display: block; margin: 0 auto;`);
          }
        }
      }
      
      // Also check for any ancestor with text-align: center
      let ancestor = parent;
      while (ancestor && ancestor !== cloned) {
        const ancestorStyle = ancestor.getAttribute('style');
        if (ancestorStyle && ancestorStyle.includes('text-align: center')) {
          const currentStyle = htmlImg.getAttribute('style') || '';
          if (!currentStyle.includes('display: block') && !currentStyle.includes('margin: 0 auto')) {
            htmlImg.setAttribute('style', `${currentStyle}; display: block; margin: 0 auto;`);
          }
          break;
        }
        ancestor = ancestor.parentElement;
      }
    });
    
    // Special handling for paragraphs containing images to preserve centering
    const paragraphs = cloned.querySelectorAll('p');
    paragraphs.forEach((p: Element) => {
      const htmlP = p as HTMLElement;
      const hasImage = htmlP.querySelector('img');
      const originalStyle = htmlP.getAttribute('style');
      
      if (hasImage && originalStyle && originalStyle.includes('text-align: center')) {
        // Ensure the paragraph maintains its centering
        const preservedStyles = this.extractImportantStyles(originalStyle);
        if (preservedStyles) {
          htmlP.setAttribute('style', preservedStyles);
        }
      }
    });
    
    return cloned;
  }

  private extractImportantStyles(styleString: string): string {
    const importantStyles = [
      'background-color', 'color', 'font-size', 'font-weight', 'font-style',
      'text-decoration', 'text-align', 'margin', 'padding', 'border',
      'width', 'height', 'display', 'position', 'vertical-align',
      'line-height', 'white-space', 'font-family', 'border-collapse',
      'overflow', 'overflow-wrap', 'list-style', 'list-style-type',
      'list-style-position', 'list-style-image', 'padding-inline-start',
      'margin-inline-start', 'text-indent', 'margin-left', 'margin-right',
      'margin-top', 'margin-bottom', 'text-align'
    ];
    
    const styles: string[] = [];
    importantStyles.forEach(prop => {
      const regex = new RegExp(`${prop}\\s*:\\s*[^;]+`, 'gi');
      const match = styleString.match(regex);
      if (match) {
        styles.push(match[0]);
      }
    });
    
    return styles.join('; ');
  }

  getAlertToolbar(): Toolbar {
    return [
      [{heading: ['h1','h2', 'h3', 'h4', 'h5', 'h6']}],
      ['bold', 'italic', 'underline', 'strike'],
      ['text_color', 'background_color'],
      ['ordered_list', 'bullet_list'],
      ['link', 'image', 'code'],
      ['blockquote', 'horizontal_rule'],
      ['format_clear']
    ]
  }
}